import { forwardRef, memo, useMemo } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS, Transform } from "@dnd-kit/utilities";
import { faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import {
  FieldArrayWithId,
  FieldError,
  FieldErrorsImpl,
  Merge,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { ReorderHandle } from "src/base-components/ReorderHandle";
import { IconButton } from "src/design-system/Icon";
import { useEntitySchemas } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { useFeatures } from "src/featureCatalogue/queries";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { isInternalSchemaDefKey } from "src/jsonSchema/validateJsonSchema";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { SchemaOptions } from "src/router/SearchParams";
import { EntitySchemaHint } from "src/schema/EntitySchemaHint";
import { SchemaEnumsEditor } from "src/schema/enumEditor/SchemaEnumsEditor";
import { decodeJsonRefPath, isJsonRefPath } from "src/schema/jsonRefUtils";
import { ObjectFieldRenderer } from "src/schema/ObjectFieldRenderer";
import { SchemaAdvancedOptions } from "src/schema/SchemaAdvancedOptions";
import { SchemaGlobalFeatures } from "src/schema/SchemaGlobalFeatures";
import { SchemaNameInput } from "src/schema/SchemaNameInput";
import { SchemaSensitiveToggle } from "src/schema/SchemaSensitiveToggle";
import { SchemaTypeSelector } from "src/schema/SchemaTypeSelector";
import {
  getFeaturesByEntity,
  PropertyUI,
  SchemaUI,
} from "src/schema/schemaMappingUtils";

type Props = {
  property: FieldArrayWithId<
    {
      schemaFields: PropertyUI[];
    },
    "schemaFields",
    "id"
  >;
  errorStatus?: Merge<FieldError, FieldErrorsImpl<PropertyUI>>;
  fieldIndex: number;
  readonly?: boolean;
  displaySensitiveToggle: boolean;
  type: SchemaOptions;
  id: string;
  openJsonSchemaWindow?: () => void;
  onDelete: () => void;
  onShowAllEnums: () => void;
};

const SchemaEditRowComponent = forwardRef<HTMLDivElement, Props>(
  (
    {
      readonly = false,
      errorStatus,
      fieldIndex,
      displaySensitiveToggle,
      type,
      id,
      openJsonSchemaWindow,
      onDelete,
      onShowAllEnums,
    },
    ref,
  ) => {
    const { control, setValue } = useFormContext<SchemaUI>();
    const { workspace } = useWorkspaceContext();
    const featureGates = useWorkspaceFeatureGates();

    const { listeners, setNodeRef, transform, transition, isDragging } =
      useSortable({
        id: id,
      });

    const style = {
      transform: CSS.Transform.toString(
        transform ? ({ ...transform, scaleY: 1 } as Transform) : null,
      ),
      transition,
    };

    const { data: entitySchemas } = useEntitySchemas({
      baseUrl: workspace.base_url!,
      options: {
        enabled: featureGates.entitiesEnabled,
      },
    });

    const { data: eventTypes } = useEventSchemas();

    const { data: features } = useFeatures({
      options: {
        enabled: featureGates.featuresEventsEnabled,
      },
    });

    const property = useWatch({
      control,
      name: `properties.${fieldIndex}`,
    });

    const propertyEnum = property?.enum;
    const propertyType = property?.type;
    const propTypeFirstComponent = propertyType[0] ?? "";
    const isObject = propTypeFirstComponent === "object";
    const isArray = propTypeFirstComponent === "array";
    const isJsonRef = isJsonRefPath(propTypeFirstComponent);
    const isSearchHighlighted = useIsFieldHighlighted(
      `${property?.fieldName}:${type}`,
    );
    const isInternalSchemaRef = isInternalSchemaDefKey(propTypeFirstComponent);
    const showObjectFieldRenderer =
      (isObject || isArray || (isJsonRef && !isInternalSchemaRef)) &&
      !isDragging;
    const decodedRef = decodeJsonRefPath(propTypeFirstComponent);
    const showGlobalFeatures =
      featureGates.featuresEventsEnabled &&
      isObject &&
      property.fieldName === "features";
    const selectedEntity =
      decodedRef?.metaType === "entity"
        ? entitySchemas?.entities.find(
            (entity) => entity._id === decodedRef.type,
          )
        : undefined;
    const selectedEvent =
      decodedRef?.metaType === "event"
        ? eventTypes?.find(
            (eventType) => eventType.internal_name === decodedRef.type,
          )
        : undefined;
    const featuresByEntity = useMemo(
      () => getFeaturesByEntity(features ?? []),
      [features],
    );
    return (
      <div
        ref={(node) => {
          setNodeRef(node);
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        className="mt-3"
        data-index={fieldIndex}
        data-loc={`schema-edit-row-${fieldIndex}`}
        style={style}
      >
        <div
          className={twJoin(
            "flex w-full flex-row items-center gap-x-2",
            isInternalSchemaRef && "mb-3",
          )}
        >
          <ReorderHandle
            color="text-gray-500"
            immutable={readonly}
            listeners={listeners}
          />
          <div className="grow">
            <SchemaNameInput
              disabled={readonly}
              errorStatus={errorStatus}
              isNew={property._isNew ?? false}
              isSearchHighlighted={isSearchHighlighted}
              value={property?.fieldName ?? ""}
              onChange={(value) => {
                setValue(`properties.${fieldIndex}.fieldName`, value, {
                  shouldValidate: true,
                });
              }}
              onSelect={() => {
                setValue(`properties.${fieldIndex}._isNew`, false);
              }}
            />
          </div>
          <div className={isInternalSchemaRef ? "w-[167px]" : "w-[140px]"}>
            <SchemaTypeSelector
              disabled={readonly}
              index={fieldIndex}
              rowName={`properties.${fieldIndex}`}
              type={type}
            />
          </div>
          <div className="flex w-auto items-center gap-x-2">
            {displaySensitiveToggle && !isInternalSchemaRef && (
              <SchemaSensitiveToggle
                disabled={readonly}
                rowName={`properties.${fieldIndex}`}
              />
            )}
            <SchemaAdvancedOptions
              disabled={readonly}
              rowName={`properties.${fieldIndex}`}
            />
            <IconButton
              dataLoc="schema-field-delete"
              disabled={readonly}
              icon={faTrashAlt}
              size="xs"
              onClick={onDelete}
            />
          </div>
        </div>
        {Array.isArray(propertyEnum) && !isDragging && (
          <SchemaEnumsEditor
            immutable={readonly}
            propertyIndex={fieldIndex}
            onShowAll={onShowAllEnums}
          />
        )}
        {entitySchemas && (
          <EntitySchemaHint
            entitySchema={selectedEntity}
            entitySchemas={entitySchemas.entities}
            eventType={selectedEvent}
            featuresByEntity={featuresByEntity}
            rootPath={`data.${property?.fieldName}`}
          />
        )}
        {showObjectFieldRenderer && (
          <ObjectFieldRenderer
            openJsonSchemaWindow={openJsonSchemaWindow}
            property={property}
            type={type}
          />
        )}
        {showGlobalFeatures && <SchemaGlobalFeatures />}
      </div>
    );
  },
);

export const SchemaEditRow = memo(SchemaEditRowComponent);
